// src/admin/SectionAdmin.jsx

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import BASE_URL from "../config";

export default function SectionAdmin() {
  const { slug } = useParams(); // e.g. "about-us", "facilities", etc.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState({
    ItemID: null,
    Title: "",
    Content: "",
    DisplayOrder: 0,
  });

  // Fetch items for this section slug
  const loadItems = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/api/content/sections/slug/${slug}/items`)
      .then((res) => setItems(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(loadItems, [slug]);

  const openNew = () => {
    setCurrent({
      ItemID: null,
      Title: "",
      Content: "",
      DisplayOrder: items.length + 1,
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setCurrent({
      ItemID: item.ItemID,
      Title:   item.Title,
      Content: item.Content,
      DisplayOrder: item.DisplayOrder,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSave = () => {
    const { ItemID, Title, Content, DisplayOrder } = current;
    const payload = { title: Title, content: Content, displayOrder: DisplayOrder };
    const base = `${BASE_URL}/api/content/sections/slug/${slug}/items`;

    const req = ItemID
      ? axios.put(`${base}/${ItemID}`, payload)
      : axios.post(base, payload);

    req
      .then(() => {
        loadItems();
        closeDialog();
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        alert("Save failed: " + (err.response?.data?.error || err.message));
      });
  };

  const handleDelete = (item) => {
    if (!window.confirm(`Delete "${item.Title}"?`)) return;
    axios
      .delete(`${BASE_URL}/api/content/sections/slug/${slug}/items/${item.ItemID}`)
      .then(loadItems)
      .catch((err) => {
        console.error(err.response?.data || err.message);
        alert("Delete failed: " + (err.response?.data?.error || err.message));
      });
  };

  // Human-friendly title from slug
  const humanTitle = slug
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" my={4}>
        <Typography variant="h4">Manage “{humanTitle}”</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add Item
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={80}>Order</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Content (truncated)</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No items found.
              </TableCell>
            </TableRow>
          )}
          {items.map((it) => (
            <TableRow key={it.ItemID}>
              <TableCell>{it.DisplayOrder}</TableCell>
              <TableCell>{it.Title}</TableCell>
              <TableCell>
                {it.Content?.length > 60 ? it.Content.slice(0, 60) + "…" : it.Content}
              </TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEdit(it)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(it)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{current.ItemID ? "Edit Item" : "Add New Item"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={current.Title}
            onChange={(e) => setCurrent((c) => ({ ...c, Title: e.target.value }))}
          />
          <TextField
            label="Content"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            value={current.Content}
            onChange={(e) => setCurrent((c) => ({ ...c, Content: e.target.value }))}
          />
          <TextField
            label="Display Order"
            type="number"
            fullWidth
            margin="normal"
            value={current.DisplayOrder}
            onChange={(e) =>
              setCurrent((c) => ({
                ...c,
                DisplayOrder: parseInt(e.target.value, 10) || 0,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {current.ItemID ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
